import { createApp } from '@backstage/frontend-defaults';
import {
  convertLegacyAppOptions,
  convertLegacyAppRoot,
} from '@backstage/core-compat-api';
import { FlatRoutes } from '@backstage/core-app-api';
import {
  IdentityProviders,
  SignInPage,
  TableProps,
} from '@backstage/core-components';
import {
  attachComponentData,
  getComponentData,
  microsoftAuthApiRef,
} from '@backstage/core-plugin-api';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {
  Permission,
  ResourcePermission,
} from '@backstage/plugin-permission-common';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityOwnerPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { NotificationsPage as BNotificationsPage } from '@backstage/plugin-notifications';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react'; // eslint-disable-line
import { SearchPage } from '@backstage/plugin-search';
import {
  DefaultTechDocsHome,
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { UnifiedThemeProvider } from '@backstage/theme';
import { simpleIconsColor } from '@dweber019/backstage-plugin-simple-icons';
import { Typography } from '@material-ui/core';
import DarkIcon from '@material-ui/icons/Brightness2';
import StarBorder from '@material-ui/icons/StarBorder';
import LightIcon from '@material-ui/icons/WbSunny';
import { visuallyHidden } from '@mui/utils';
import { CatalogGraphPage } from '@tranzact/backstage-plugin-catalog-graph';
import { CatalogHierarchyPage } from '@tranzact/backstage-plugin-catalog-hierarchy';
import { CatalogStudioPage } from '@tranzact/backstage-plugin-catalog-studio';
import { CostInsightsPage } from '@tranzact/backstage-plugin-cost-insightz';
import { LimePage } from '@tranzact/backstage-plugin-lime';
import { NotificationsPage } from '@tranzact/backstage-plugin-notifications';
import { SatsukiPage } from '@tranzact/backstage-plugin-satsuki';
import { TfnTestingToolPage } from '@tranzact/backstage-plugin-tfn-testing-tool';
import { SidiousPage } from '@tranzact/backstage-plugin-sidious';
import { runtest } from '@tranzact/backstage-plugin-sidious-permissions-common';
import { ExplorePage } from '@tranzact/plugin-explore';
import { ReactElement } from 'react';
import { Route } from 'react-router-dom';
import { apisModule } from './apis';
import { legacyPluginFeatures } from './legacyPlugins';
import { layoutModule } from './modules/layout';
import { navModule } from './modules/nav';
import { rootElementsModule } from './modules/rootElements';
import { entityPage } from './components/catalog/EntityPage';
import { EntitySubDomainPicker } from './components/CatalogFilter/EntitySubDomainPicker';
import { YellowStar } from './components/CatalogGrid/defaultActions';
import { CatalogImagePage } from './components/CatalogImage/CatalogImage';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import { HomePage } from './components/home/HomePage';
import { searchPage } from './components/search/SearchPage';
import TechRadarCustomPage from './components/tech-radar/TechRadarCustomPage';
import { GithubTeamsExtension } from './lib/GithubTeams';
import { GithubTeamsMultiselectExtension } from './lib/GithubTeamsMultiselect';
import { GithubTemplateRepositoriesExtension } from './lib/GithubTemplateRepositories';
import { TeamcityBuildTemplatesExtension } from './lib/TeamcityBuildTemplates';
import { TeamcityProjectsExtension } from './lib/TeamcityProjects';
import { TextFieldWithPrefixAndSufixExtension } from './lib/TextFieldWithPrefixAndSufix/TextFieldWithPrefixAndSufix';
import { darkTheme, lightTheme } from './theme/myTheme';
import { TeamcityBuildTypeConfigurationExtension } from './lib/TeamcityBuildTypeConfiguration';

/** Check if the program is running in development mode. */
const isDevelopment = process.env.NODE_ENV === 'development';

function getProviders(): IdentityProviders {
  const providers: IdentityProviders = [
    {
      id: 'azure-auth-provider',
      title: 'Login with Microsoft',
      message: 'Sign in using your Microsoft Account',
      apiRef: microsoftAuthApiRef,
    },
  ];
  if (isDevelopment) providers.push('guest');
  return providers;
}

const appOptionsModule = convertLegacyAppOptions({
  icons: simpleIconsColor,
  components: {
    SignInPage: props => <SignInPage {...props} providers={getProviders()} />,
  },
  themes: [
    {
      id: 'light-theme',
      title: 'Light Theme',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={lightTheme} children={children} />
      ),
    },
    {
      id: 'dark-theme',
      title: 'Dark Theme',
      variant: 'dark',
      icon: <DarkIcon />,
      Provider: ({ children }) => (
        <UnifiedThemeProvider theme={darkTheme} children={children} />
      ),
    },
  ],
});

const tableColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
  const columns = [
    ...CatalogTable.defaultColumnsFunc(entityListContext),
  ].filter(
    column =>
      // exclude the columns that are not needed here!
      column.field !== 'entity.spec.lifecycle' &&
      column.field !== 'entity.metadata.description',
  );
  const tagsColumnIndex = columns.findIndex(
    column => column.field === 'entity.metadata.tags',
  );
  if (tagsColumnIndex !== -1) {
    columns[tagsColumnIndex].cellStyle = {
      whiteSpace: 'normal',
      minWidth: '350px',
    };
  }
  // https://backstage.io/docs/features/software-catalog/catalog-customization/#adding-columns-to-a-custom-or-specific-kind
  return columns;
};

const tableActionsProps: TableProps<CatalogTableRow>['actions'] = [
  ({ entity }) => {
    const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
    const isStarred = isStarredEntity(entity);
    const title = isStarred ? 'Remove from favorites' : 'Add to favorites';
    return {
      icon: () => (
        <>
          <Typography style={visuallyHidden}>{title}</Typography>
          {isStarred ? <YellowStar /> : <StarBorder />}
        </>
      ),
      tooltip: title,
      onClick: () => toggleStarredEntity(entity),
    };
  },
];

/**
 * Renders a routable page behind a permission check without hiding it from
 * `convertLegacyAppRoot`.
 *
 * The compat shim reads `core.mountPoint` and `core.plugin` off the element
 * handed to a `Route` and nowhere else, so a `<RequirePermission>` sitting in
 * between leaves the route ref unregistered, and the page throws "was not
 * discovered in the app element tree" the moment it renders. Copying that data
 * onto the wrapper keeps the route discoverable while the gate still runs.
 */
function permissionProtected(
  page: ReactElement,
  permission: Exclude<Permission, ResourcePermission>,
): ReactElement {
  const Protected = () => (
    <RequirePermission permission={permission}>{page}</RequirePermission>
  );
  for (const key of ['core.mountPoint', 'core.plugin'] as const) {
    const data = getComponentData<unknown>(page, key);
    if (data) {
      attachComponentData(Protected, key, data);
    }
  }
  return <Protected />;
}

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    {/*
      A Route's element must be the routable extension itself, with no wrapper --
      not even a fragment. See permissionProtected above for why, and for the way
      around it when a page also needs a permission gate.
    */}
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          pagination={{ mode: 'offset', limit: 20 }}
          columns={tableColumnsFunc}
          filters={
            <>
              <EntityKindPicker
                initialFilter="system"
                allowedKinds={[
                  'domain',
                  'system',
                  'component',
                  'resource',
                  'api',
                  'group',
                  'user',
                ]}
              />
              <EntityTypePicker />
              <UserListPicker initialFilter="all" />
              <EntitySubDomainPicker initialFilter="parent" />
              <EntityOwnerPicker />
              <EntityTagPicker />
              <EntityLifecyclePicker />
            </>
          }
          actions={tableActionsProps}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DefaultTechDocsHome />
    </Route>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <TeamcityBuildTemplatesExtension />
        <TeamcityBuildTypeConfigurationExtension />
        <GithubTeamsExtension />
        <GithubTeamsMultiselectExtension />
        <GithubTemplateRepositoriesExtension />
        <TeamcityProjectsExtension />
        <TextFieldWithPrefixAndSufixExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/cost-insights" element={<CostInsightsPage />} />
    <Route
      path="/tech-radar"
      element={<TechRadarCustomPage width={1800} height={900} />}
    />
    <Route
      path="/catalog-import"
      element={permissionProtected(
        <CatalogImportPage />,
        catalogEntityCreatePermission,
      )}
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route
      path="/catalog-graph"
      element={<CatalogGraphPage showArrowHeads mergeRelations={false} />}
    />
    <Route path="/explore" element={<ExplorePage />} />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route
      path="/sidious"
      element={permissionProtected(<SidiousPage />, runtest)}
    />
    <Route path="/catalogImage" element={<CatalogImagePage />} />

    <Route path="/lime" element={<LimePage />} />
    <Route path="/satsuki" element={<SatsukiPage />} />
    <Route path="/tfn-testing-tool" element={<TfnTestingToolPage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/notification" element={<BNotificationsPage />} />
    <Route path="/catalog-hierarchy" element={<CatalogHierarchyPage />} />
    <Route path="/catalog-studio" element={<CatalogStudioPage />} />
  </FlatRoutes>
);

// FlatRoutes alone, never an app root: given an app root the shim overrides
// `app/layout` and disables `app/nav`, and then no nav extension can render.
const collectedLegacyFeatures = convertLegacyAppRoot(routes);

const app = createApp({
  features: [
    apisModule,
    appOptionsModule,
    layoutModule,
    navModule,
    rootElementsModule,
    ...legacyPluginFeatures,
    ...collectedLegacyFeatures,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

export default app.createRoot();
